<script setup>
import { Link, useForm } from '@inertiajs/vue3'
import { AlertCircle, BookOpen, UserPlus } from 'lucide-vue-next'
import { computed, ref } from 'vue'
import PrivacyPolicyContent from '@/Components/Legal/PrivacyPolicyContent.vue'
import StudentTermsContent from '@/Components/Legal/StudentTermsContent.vue'
import LegalModal from '@/Components/Modals/LegalModal.vue'
import { Alert, AlertDescription } from '@/Components/ui/alert'
import AltchaField from '@/Components/Forms/AltchaField.vue'
import { Button } from '@/Components/ui/button'
import { Checkbox } from '@/Components/ui/checkbox'
import { trackEvent } from '@/composables/useAnalytics'
import AppLayout from '@/Layouts/AppLayout.vue'
import { store } from '@/routes/signup'

const altchaField = ref(null)
const form = useForm({
  altcha: '',
  fullname: '',
  email: '',
  password: '',
  password_confirmation: '',
  terms: false,
})

const showTermsModal = ref(false)
const showPrivacyModal = ref(false)

const submit = () => {
  form.submit(store(), {
    onFinish: () => altchaField.value?.reset(),
    onSuccess: () => {
      if (form.wasSuccessful) {
        trackEvent('completeRegistration', { method: 'email' })
      }
    },
  })
}

const hasErrors = computed(() => Object.keys(form.errors).length > 0)
</script>

<template>
  <AppLayout>
    <div class="flex items-center justify-center min-h-[calc(100vh-200px)] py-12 px-4 sm:px-6 lg:px-8">
      <div class="w-full max-w-md bg-white rounded-2xl shadow-lg p-10">
        <!-- Header -->
        <div class="mb-8">
          <div class="flex justify-center mb-6">
            <div class="w-12 h-12 bg-green-600 rounded-xl flex items-center justify-center text-white">
              <UserPlus class="w-6 h-6" />
            </div>
          </div>
          <h1 class="text-2xl font-bold text-gray-900 text-center mb-2">Create an Account</h1>
          <p class="text-gray-600 text-center">Join thousands of students learning with Bindle</p>
        </div>

        <!-- Become a tutor banner -->
        <div class="mb-6 p-3 bg-blue-50 rounded-lg flex items-start space-x-3">
          <BookOpen class="w-5 h-5 text-blue-600 mt-0.5" />
          <div class="text-sm text-blue-900">
            Want to become a tutor?
            <Link href="/become-a-tutor" class="font-semibold text-blue-700 hover:text-blue-800 underline">
              Start here
            </Link>
          </div>
        </div>

        <form @submit.prevent="submit" class="space-y-6">
          <!-- General Error Alert -->
          <Alert v-if="hasErrors" variant="destructive">
            <AlertCircle class="h-4 w-4" />
            <AlertDescription>
              Please correct the errors below.
            </AlertDescription>
          </Alert>

          <!-- Full Name Field -->
          <div>
            <label for="fullname" class="block text-sm font-semibold text-gray-700 mb-3">
              Full Name <span class="text-red-500">*</span>
            </label>
            <input
              id="fullname"
              type="text"
              placeholder="John Doe"
              v-model="form.fullname"
              required
              autofocus
              :class="[
                'w-full px-5 py-4 border-2 rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-transparent outline-none transition-all text-gray-900',
                form.errors.fullname ? 'border-red-500' : 'border-gray-200'
              ]"
            />
            <p v-if="form.errors.fullname" class="text-sm text-red-500 mt-1">{{ form.errors.fullname }}</p>
          </div>

          <!-- Email Field -->
          <div>
            <label for="email" class="block text-sm font-semibold text-gray-700 mb-3">
              Email <span class="text-red-500">*</span>
            </label>
            <input
              id="email"
              type="email"
              placeholder="m@example.com"
              v-model="form.email"
              required
              :class="[
                'w-full px-5 py-4 border-2 rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-transparent outline-none transition-all text-gray-900',
                form.errors.email ? 'border-red-500' : 'border-gray-200'
              ]"
            />
            <p v-if="form.errors.email" class="text-sm text-red-500 mt-1">{{ form.errors.email }}</p>
          </div>

          <!-- Password Field -->
          <div>
            <label for="password" class="block text-sm font-semibold text-gray-700 mb-3">
              Password <span class="text-red-500">*</span>
            </label>
            <input
              id="password"
              type="password"
              v-model="form.password"
              required
              :class="[
                'w-full px-5 py-4 border-2 rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-transparent outline-none transition-all text-gray-900',
                form.errors.password ? 'border-red-500' : 'border-gray-200'
              ]"
            />
            <p v-if="form.errors.password" class="text-sm text-red-500 mt-1">{{ form.errors.password }}</p>
          </div>

          <!-- Confirm Password Field -->
          <div>
            <label for="password_confirmation" class="block text-sm font-semibold text-gray-700 mb-3">
              Confirm Password <span class="text-red-500">*</span>
            </label>
            <input
              id="password_confirmation"
              type="password"
              v-model="form.password_confirmation"
              required
              :class="[
                'w-full px-5 py-4 border-2 rounded-xl focus:ring-2 focus:ring-brand-blue focus:border-transparent outline-none transition-all text-gray-900',
                form.errors.password_confirmation ? 'border-red-500' : 'border-gray-200'
              ]"
            />
            <p v-if="form.errors.password_confirmation" class="text-sm text-red-500 mt-1">{{ form.errors.password_confirmation }}</p>
          </div>

          <!-- Terms Checkbox -->
          <div class="flex items-start space-x-2">
            <Checkbox id="terms" :checked="form.terms" @update:checked="form.terms = $event" required />
            <label
              for="terms"
              class="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 mt-0.5"
              :class="{'text-red-500': form.errors.terms}"
            >
              I agree to the
              <button type="button" @click.prevent="showTermsModal = true" class="text-blue-600 hover:underline">Terms of Service</button>
              and
              <button type="button" @click.prevent="showPrivacyModal = true" class="text-blue-600 hover:underline">Privacy Policy</button>
            </label>
          </div>
          <p v-if="form.errors.terms" class="text-sm text-red-500">{{ form.errors.terms }}</p>

          <div>
            <AltchaField ref="altchaField" v-model="form.altcha" />
            <p v-if="form.errors.altcha" class="text-sm text-red-500 mt-1">{{ form.errors.altcha }}</p>
          </div>

          <Button class="w-full bg-green-600 hover:bg-green-700 h-auto px-5 py-4 text-base font-semibold rounded-xl" :disabled="form.processing || !form.altcha">
            Create Account
          </Button>
        </form>

        <!-- Footer -->
        <div class="mt-8 text-center text-sm text-gray-600">
          Already have an account?
          <Link href="/login" class="font-semibold text-green-600 hover:text-green-500">
            Sign in
          </Link>
        </div>
      </div>
    </div>

    <!-- Legal Modals -->
    <LegalModal :show="showTermsModal" title="Student Terms & Conditions" @close="showTermsModal = false">
      <StudentTermsContent />
    </LegalModal>

    <LegalModal :show="showPrivacyModal" title="Privacy Policy" @close="showPrivacyModal = false">
      <PrivacyPolicyContent />
    </LegalModal>
  </AppLayout>
</template>
